/**
 * VoiceCommandSettings - Voice Command Shortcuts Customization Component
 * 
 * Allows users to create custom voice command aliases, manage their shortcuts,
 * and configure voice settings like wake word and TTS speed.
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Slider } from "./ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { ScrollArea } from "./ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Mic,
  Plus,
  Trash2,
  Edit,
  Volume2,
  Settings,
  Command,
  ArrowRight,
  RefreshCw,
  Search,
  Info,
  CheckCircle,
  XCircle,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Category colors for visual distinction
const CATEGORY_COLORS = {
  navigation: "bg-blue-500",
  documentation: "bg-green-500",
  action: "bg-purple-500",
  communication: "bg-orange-500",
  custom: "bg-pink-500",
  system: "bg-gray-500",
};

// Available target commands grouped by category
const TARGET_COMMANDS = {
  navigation: [
    { value: "dashboard", label: "Dashboard" },
    { value: "reports", label: "Reports" },
    { value: "hospice os", label: "Hospice OS" },
    { value: "calendar", label: "Calendar" },
    { value: "medications", label: "Medications" },
    { value: "patient portal", label: "Patient Portal" },
    { value: "family portal", label: "Family Portal" },
    { value: "idg summary", label: "IDG Summary" },
    { value: "workforce", label: "Workforce" },
    { value: "analytics", label: "Analytics" },
    { value: "billing", label: "Billing" },
    { value: "quality", label: "Quality & Compliance" },
    { value: "hope", label: "HOPE Assessment" },
    { value: "referral", label: "Referral Portal" },
  ],
  documentation: [
    { value: "new visit note", label: "New Visit Note" },
    { value: "phone log", label: "Phone Log" },
    { value: "pain management", label: "Pain Management" },
    { value: "wound care", label: "Wound Care" },
    { value: "care plan", label: "Care Plan" },
    { value: "idg review", label: "IDG Review" },
    { value: "idg dictation", label: "IDG Dictation" },
    { value: "smart documentation", label: "Smart Documentation" },
    { value: "vital signs", label: "Vital Signs" },
    { value: "symptoms", label: "Symptom Assessment" },
    { value: "incident report", label: "Incident Report" },
    { value: "consent form", label: "Consent Form" },
    { value: "recertification", label: "Recertification" },
  ],
  action: [
    { value: "schedule visit", label: "Schedule Visit" },
    { value: "dme order", label: "DME Order" },
    { value: "physician order", label: "Physician Order" },
    { value: "discharge", label: "Discharge" },
    { value: "intake", label: "New Patient Intake" },
    { value: "export report", label: "Export Report" },
  ],
  communication: [
    { value: "family message", label: "Family Message" },
  ],
};

export default function VoiceCommandSettings({ userId, onClose }) {
  const [shortcuts, setShortcuts] = useState([]);
  const [defaultCommands, setDefaultCommands] = useState([]);
  const [profile, setProfile] = useState({
    wake_word: "Hey Goodwin",
    voice_speed: 1.0,
    confirmation_sounds: true,
    auto_submit: false,
    language: "en-US",
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingShortcut, setEditingShortcut] = useState(null);
  const [newShortcut, setNewShortcut] = useState({
    alias: "",
    target_command: "",
    category: "custom",
  });

  // Fetch user's shortcuts and defaults
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [userRes, defaultsRes] = await Promise.all([
        axios.get(`${API_URL}/api/voice-commands/user/${userId}`),
        axios.get(`${API_URL}/api/voice-commands/defaults`),
      ]);
      
      setShortcuts(userRes.data.shortcuts || []);
      setProfile(userRes.data.profile || profile);
      setDefaultCommands(defaultsRes.data.commands || []);
    } catch (err) {
      console.error("Failed to fetch voice commands:", err);
      toast.error("Failed to load voice command settings");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId, fetchData]);

  // Create new shortcut
  const handleCreateShortcut = async () => {
    if (!newShortcut.alias || !newShortcut.target_command) {
      toast.error("Please fill in both alias and target command");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/voice-commands/user/${userId}/shortcut`, newShortcut);
      toast.success(`Voice shortcut "${newShortcut.alias}" created!`);
      setShowAddDialog(false);
      setNewShortcut({ alias: "", target_command: "", category: "custom" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create shortcut");
    }
  };

  // Delete shortcut
  const handleDeleteShortcut = async (shortcutId) => {
    try {
      await axios.delete(`${API_URL}/api/voice-commands/user/${userId}/shortcut/${shortcutId}`);
      toast.success("Shortcut deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete shortcut");
    }
  };

  // Toggle shortcut enabled/disabled
  const handleToggleShortcut = async (shortcut) => {
    try {
      await axios.put(`${API_URL}/api/voice-commands/user/${userId}/shortcut/${shortcut.id}`, {
        enabled: !shortcut.enabled,
      });
      fetchData();
    } catch (err) {
      toast.error("Failed to update shortcut");
    }
  };

  // Update voice profile
  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API_URL}/api/voice-commands/user/${userId}/profile`, profile);
      toast.success("Voice settings saved!");
    } catch (err) {
      toast.error("Failed to update settings");
    }
  };

  // Filter commands by search
  const filteredDefaults = defaultCommands.filter(
    (cmd) =>
      cmd.phrase.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cmd.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredShortcuts = shortcuts.filter(
    (s) =>
      s.alias.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.target_command.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <Tabs defaultValue="shortcuts" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="shortcuts" className="gap-2">
            <Wand2 className="w-4 h-4" />
            My Shortcuts
          </TabsTrigger>
          <TabsTrigger value="defaults" className="gap-2">
            <Command className="w-4 h-4" />
            All Commands
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="w-4 h-4" />
            Voice Settings
          </TabsTrigger>
        </TabsList>

        {/* My Shortcuts Tab */}
        <TabsContent value="shortcuts" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mic className="w-5 h-5 text-primary" />
                    Custom Voice Shortcuts
                  </CardTitle>
                  <CardDescription>
                    Create your own voice command aliases for faster navigation
                  </CardDescription>
                </div>
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" data-testid="add-shortcut-btn">
                      <Plus className="w-4 h-4" />
                      Add Shortcut
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Voice Shortcut</DialogTitle>
                      <DialogDescription>
                        Create a custom phrase that triggers an existing command
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="alias">Your Custom Phrase</Label>
                        <Input
                          id="alias"
                          placeholder="e.g., 'my notes' or 'go home'"
                          value={newShortcut.alias}
                          onChange={(e) =>
                            setNewShortcut({ ...newShortcut, alias: e.target.value })
                          }
                          data-testid="shortcut-alias-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          What you will say to trigger the command
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="target">Target Command</Label>
                        <Select
                          value={newShortcut.target_command}
                          onValueChange={(value) =>
                            setNewShortcut({ ...newShortcut, target_command: value })
                          }
                        >
                          <SelectTrigger data-testid="target-command-select">
                            <SelectValue placeholder="Select a command to trigger" />
                          </SelectTrigger>
                          <SelectContent>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                              Navigation
                            </div>
                            {TARGET_COMMANDS.navigation.map((cmd) => (
                              <SelectItem key={cmd.value} value={cmd.value}>
                                {cmd.label}
                              </SelectItem>
                            ))}
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">
                              Documentation
                            </div>
                            {TARGET_COMMANDS.documentation.map((cmd) => (
                              <SelectItem key={cmd.value} value={cmd.value}>
                                {cmd.label}
                              </SelectItem>
                            ))}
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">
                              Actions
                            </div>
                            {TARGET_COMMANDS.action.map((cmd) => (
                              <SelectItem key={cmd.value} value={cmd.value}>
                                {cmd.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                        <p className="text-sm flex items-center gap-2">
                          <Info className="w-4 h-4 text-blue-500" />
                          <span>
                            Example: Say <strong>&quot;{newShortcut.alias || "my notes"}&quot;</strong> to trigger{" "}
                            <strong>{newShortcut.target_command || "new visit note"}</strong>
                          </span>
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateShortcut} data-testid="create-shortcut-btn">
                        Create Shortcut
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {shortcuts.length === 0 ? (
                <div className="text-center py-8">
                  <Mic className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No custom shortcuts yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Click &quot;Add Shortcut&quot; to create your first voice alias
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Your Phrase</TableHead>
                      <TableHead></TableHead>
                      <TableHead>Triggers</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Enabled</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shortcuts.map((shortcut) => (
                      <TableRow key={shortcut.id}>
                        <TableCell className="font-medium">
                          &quot;{shortcut.alias}&quot;
                        </TableCell>
                        <TableCell>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </TableCell>
                        <TableCell>{shortcut.target_command}</TableCell>
                        <TableCell>
                          <Badge
                            className={`${
                              CATEGORY_COLORS[shortcut.category] || CATEGORY_COLORS.custom
                            } text-white`}
                          >
                            {shortcut.category}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={shortcut.enabled}
                            onCheckedChange={() => handleToggleShortcut(shortcut)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteShortcut(shortcut.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Commands Tab */}
        <TabsContent value="defaults" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Command className="w-5 h-5 text-primary" />
                Available Voice Commands ({defaultCommands.length})
              </CardTitle>
              <CardDescription>
                All built-in voice commands you can use
              </CardDescription>
              <div className="pt-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search commands..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Voice Phrase</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Category</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDefaults.map((cmd, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          &quot;{cmd.phrase}&quot;
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {cmd.action}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`${
                              CATEGORY_COLORS[cmd.category] || CATEGORY_COLORS.system
                            } text-white`}
                          >
                            {cmd.category}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" />
                Voice Settings
              </CardTitle>
              <CardDescription>
                Customize how voice commands work for you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Wake Word */}
              <div className="space-y-2">
                <Label htmlFor="wake-word">Wake Word</Label>
                <Input
                  id="wake-word"
                  value={profile.wake_word}
                  onChange={(e) =>
                    setProfile({ ...profile, wake_word: e.target.value })
                  }
                  placeholder="Hey Goodwin"
                />
                <p className="text-xs text-muted-foreground">
                  Say this phrase to activate voice commands from any page
                </p>
              </div>

              {/* Voice Speed */}
              <div className="space-y-2">
                <Label>Text-to-Speech Speed: {profile.voice_speed}x</Label>
                <Slider
                  value={[profile.voice_speed]}
                  onValueChange={([value]) =>
                    setProfile({ ...profile, voice_speed: value })
                  }
                  min={0.5}
                  max={2}
                  step={0.1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Slower</span>
                  <span>Faster</span>
                </div>
              </div>

              {/* Language */}
              <div className="space-y-2">
                <Label htmlFor="language">Recognition Language</Label>
                <Select
                  value={profile.language}
                  onValueChange={(value) =>
                    setProfile({ ...profile, language: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en-US">English (US)</SelectItem>
                    <SelectItem value="en-GB">English (UK)</SelectItem>
                    <SelectItem value="es-ES">Spanish</SelectItem>
                    <SelectItem value="fr-FR">French</SelectItem>
                    <SelectItem value="de-DE">German</SelectItem>
                    <SelectItem value="zh-CN">Chinese (Simplified)</SelectItem>
                    <SelectItem value="vi-VN">Vietnamese</SelectItem>
                    <SelectItem value="ko-KR">Korean</SelectItem>
                    <SelectItem value="tl-PH">Tagalog</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Toggles */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="confirmation-sounds">Confirmation Sounds</Label>
                    <p className="text-xs text-muted-foreground">
                      Play a sound when commands are recognized
                    </p>
                  </div>
                  <Switch
                    id="confirmation-sounds"
                    checked={profile.confirmation_sounds}
                    onCheckedChange={(checked) =>
                      setProfile({ ...profile, confirmation_sounds: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto-submit">Auto-Execute Commands</Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically run commands without confirmation
                    </p>
                  </div>
                  <Switch
                    id="auto-submit"
                    checked={profile.auto_submit}
                    onCheckedChange={(checked) =>
                      setProfile({ ...profile, auto_submit: checked })
                    }
                  />
                </div>
              </div>

              <Button onClick={handleUpdateProfile} className="w-full gap-2">
                <CheckCircle className="w-4 h-4" />
                Save Voice Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
